import { useEffect, useState, useCallback } from "react";

import { useTranslation } from "react-i18next";

import apiClient from "../../helpers/apiClient";
import { regularAddonApi } from "./lib/addonApi";

import CreateRegularAddon from "./CreateRegularAddon";

import AddonsHeader from "./components/AddonsHeader";
import RegularAddons from "./components/RegularAddons";
import { useToast } from "../../contexts/ToastContext";
import deleteFile from "../../utilities/deleteFile";
import useOpenModalParam from "../../hooks/useOpenModalParam";

export default function Addons() {
    const { t } = useTranslation(["addons"]);
    const [isRegularModalOpen, setIsRegularModalOpen] = useState(false);
    const [addonToEditRegular, setAddonToEditRegular] = useState(null);
    const [regularAddons, setRegularAddons] = useState([]);
    const [initialFetching, setInitialFetching] = useState(true);
    const [regularPagination, setRegularPagination] = useState({
        currentPage: 1,
        pageSize: 10,
        total: 0,
    });
    const toast = useToast();

    const fetchRegularData = useCallback(
        async (page = 1, limit = 10) => {
            try {
                setInitialFetching(true);
                const response = await apiClient.get(regularAddonApi.list(page, limit));
                const newData = response?.data?.data?.result ?? response?.data?.result;
                const totalItems = response?.data?.data?.meta?.total ?? response?.data?.meta?.total ?? 0;
                setRegularAddons(newData ?? []);
                setRegularPagination((prev) => ({ ...prev, total: totalItems }));
            } catch (_error) {
                toast.error({
                    message: t("fetchError"),
                    description: t("failedToFetchAddons"),
                });
            } finally {
                setInitialFetching(false);
            }
        },
        [toast, t],
    );

    useEffect(() => {
        fetchRegularData(regularPagination.currentPage, regularPagination.pageSize);
    }, [fetchRegularData, regularPagination.currentPage, regularPagination.pageSize]);

    const openCreateRegularModal = useCallback(() => {
        setAddonToEditRegular(null);
        setIsRegularModalOpen(true);
    }, []);

    useOpenModalParam("createAddOn", openCreateRegularModal);

    const handleRegularModalState = (state) => {
        if (typeof state === "object" && state !== null) {
            setIsRegularModalOpen(state.isOpen);
            setAddonToEditRegular(state.addonToEdit ?? null);
        } else {
            setIsRegularModalOpen(!!state);
            if (!state) setAddonToEditRegular(null);
        }
    };
    const handleDeleteRegularAddon = useCallback(
        async (addon) => {
            try {
                const response = await apiClient.delete(regularAddonApi.delete(addon.id));
                if (addon?.image) {
                    await deleteFile(addon.image);
                }
                if (response?.data?.status === 200 || response?.data?.success) {
                    toast.success({ message: t("addonDeletedSuccess") });
                    let newPage = regularPagination.currentPage;
                    if (regularAddons.length === 1 && regularPagination.currentPage > 1) {
                        newPage = regularPagination.currentPage - 1;
                        setRegularPagination((prev) => ({ ...prev, currentPage: newPage }));
                    }
                    fetchRegularData(newPage, regularPagination.pageSize);
                    return true;
                }
                return false;
            } catch (_error) {
                toast.error({ message: t("_addonDetails.failedToDelete") });
                return false;
            }
        },
        [regularPagination.currentPage, regularPagination.pageSize, regularAddons.length, fetchRegularData, t, toast],
    );

    return (
        <>
            <div className="min-w-0">
                <AddonsHeader total={regularPagination.total} onCreateRegular={openCreateRegularModal} />

                <RegularAddons
                    regularAddons={regularAddons}
                    initialFetching={initialFetching}
                    handleModalState={handleRegularModalState}
                    handleDeleteAddon={handleDeleteRegularAddon}
                    pagination={regularPagination}
                    setPagination={setRegularPagination}
                />
            </div>

            <CreateRegularAddon
                open={isRegularModalOpen}
                onClose={() => handleRegularModalState(false)}
                addonToEdit={addonToEditRegular}
                onSuccess={() => fetchRegularData(regularPagination.currentPage, regularPagination.pageSize)}
                pagination={regularPagination}
            />
        </>
    );
}
